import { useEffect, useState } from "react";
import { toast } from "sonner";
import type { Lang } from "../../lib/i18n";
import { pick } from "../../lib/i18n";
import { getCareerPaths, subscribeCareerPaths, saveCareerPaths, resetCareerPaths, newCareerPath, isBuiltInPath, type CareerPath } from "../../data/careerPaths";
import { logAudit } from "../lib/audit";

/** Career Paths editor tab (pt) — icon/id/name/description per path, reorder, save, reset. */
export function PathsTab({ lang }: { lang: Lang }) {
  const t = (ar: string, en: string) => pick(lang, ar, en);
  const [paths, setPaths] = useState<CareerPath[]>(() => getCareerPaths());
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => subscribeCareerPaths(() => { setPaths([...getCareerPaths()]); setDirty(false); }), []);

  const patch = (i: number, p: Partial<CareerPath>) => { setPaths((list) => list.map((row, idx) => (idx === i ? { ...row, ...p } : row))); setDirty(true); };
  const add = () => { setPaths((list) => [...list, { ...newCareerPath(), id: "path-" + Date.now().toString(36) }]); setDirty(true); };
  const remove = (i: number) => {
    if (confirm(t("حذف هذا المسار؟ الأسئلة المرتبطة بيه هتستمر لكن لن تظهر للموظفين.", "Delete this path? Linked Q&A items will stay but won't be shown."))) {
      setPaths((list) => list.filter((_, idx) => idx !== i));
      setDirty(true);
    }
  };
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= paths.length) return;
    setPaths((list) => { const next = [...list]; [next[i], next[j]] = [next[j], next[i]]; return next; });
    setDirty(true);
  };

  async function save() {
    const seen = new Set<string>();
    for (const p of paths) {
      if (!p.id.trim()) return toast.error(t("كل مسار لازم يكون له معرّف.", "Every path needs an id."));
      if (seen.has(p.id)) return toast.error(t(`معرّف مكرر: ${p.id}`, `Duplicate id: ${p.id}`));
      seen.add(p.id);
      if (!p.ar.trim() || !p.en.trim()) return toast.error(t("املأ الاسم العربي والإنجليزي لكل مسار.", "Fill the Arabic and English name for every path."));
    }
    setSaving(true);
    const res = await saveCareerPaths(paths);
    setSaving(false);
    if (res.ok) { setDirty(false); toast.success(t("تم حفظ المسارات", "Paths saved")); logAudit("config_imported", `Paths saved (${paths.length} paths)`); }
    else toast.error(t("تعذّر الحفظ: ", "Save failed: ") + (res.error || ""));
  }
  async function reset() {
    if (!confirm(t("استعادة المسارات الافتراضية؟ أي تعديلات مخصصة ستُفقد.", "Restore default paths? Custom changes will be lost."))) return;
    const res = await resetCareerPaths();
    res.ok ? toast.success(t("تمت الاستعادة", "Reset")) : toast.error(t("تعذّر الاستعادة: ", "Reset failed: ") + (res.error || ""));
  }

  return (
    <div className="kd-admin-paths">
      <div className="kd-admin-toolbar">
        <button className="kd-btn-primary kd-btn-gold" onClick={add}>+ {t("إضافة مسار جديد", "Add New Path")}</button>
        <button className="kd-btn-primary" onClick={save} disabled={!dirty || saving}>{saving ? t("جارٍ الحفظ...", "Saving...") : t("حفظ التغييرات", "Save Changes")}</button>
        <button className="kd-btn-secondary kd-btn-danger" onClick={reset}>⟲ {t("استعادة الافتراضي", "Reset to Defaults")}</button>
        <span className="kd-admin-path-count">
          {t("عدد المسارات:", "Total paths:")} <strong>{paths.length}</strong>
          {dirty && <span className="kd-admin-dirty-dot"> · {t("تغييرات غير محفوظة", "Unsaved changes")}</span>}
        </span>
      </div>
      <p className="kd-admin-hint">
        {t(
          "يستخدم الموظف هذه المسارات في الخطوة الثالثة لاختيار القسم المناسب لعمله. الـ ID بيُستخدم داخلياً لربط الأنظمة والأسئلة بالمسار، فلو غيّرته قد تُفقد هذه الروابط.",
          "Employees pick from these paths at Step 3. The ID is used internally to link Systems and Q&A to the path, so changing it may break those links.",
        )}
      </p>
      <div className="kd-admin-paths-list">
        {paths.map((p, i) => {
          const builtIn = isBuiltInPath(p.id);
          return (
            <div className="kd-admin-path-row" key={i}>
              <div className="kd-admin-path-reorder">
                <button className="kd-admin-action-btn" onClick={() => move(i, -1)} disabled={i === 0} title={t("أعلى", "Up")}>↑</button>
                <span className="kd-admin-path-order">{i + 1}</span>
                <button className="kd-admin-action-btn" onClick={() => move(i, 1)} disabled={i === paths.length - 1} title={t("أسفل", "Down")}>↓</button>
              </div>
              <div className="kd-admin-path-fields">
                <div className="kd-admin-grid-3">
                  <label><span>{t("الأيقونة", "Icon")}</span><input type="text" value={p.icon} onChange={(e) => patch(i, { icon: e.target.value })} maxLength={4} style={{ fontSize: "1.2rem", textAlign: "center" }} /></label>
                  <label style={{ gridColumn: "span 2" }}>
                    <span>{t("المعرّف (ID)", "Identifier (ID)")}{builtIn && <small className="kd-admin-default-tag"> · {t("افتراضي", "built-in")}</small>}</span>
                    <input type="text" value={p.id} onChange={(e) => patch(i, { id: e.target.value.trim().replace(/[^a-z0-9-]/gi, "-").toLowerCase() })} disabled={builtIn} placeholder="finance, hr-admin, ..." style={{ fontFamily: "var(--kd-font-en)" }} />
                  </label>
                </div>
                <div className="kd-admin-grid-2">
                  <label><span>{t("الاسم بالعربية", "Name (Arabic)")}</span><input type="text" value={p.ar} onChange={(e) => patch(i, { ar: e.target.value })} dir="rtl" /></label>
                  <label><span>{t("الاسم بالإنجليزية", "Name (English)")}</span><input type="text" value={p.en} onChange={(e) => patch(i, { en: e.target.value })} dir="ltr" /></label>
                </div>
                <div className="kd-admin-grid-2">
                  <label><span>{t("الوصف بالعربية", "Description (Arabic)")}</span><textarea rows={2} value={p.descAr} onChange={(e) => patch(i, { descAr: e.target.value })} dir="rtl" /></label>
                  <label><span>{t("الوصف بالإنجليزية", "Description (English)")}</span><textarea rows={2} value={p.descEn} onChange={(e) => patch(i, { descEn: e.target.value })} dir="ltr" /></label>
                </div>
              </div>
              <div className="kd-admin-path-actions">
                <button className="kd-admin-action-btn kd-admin-action-danger" onClick={() => remove(i)} title={t("حذف", "Delete")}>✕</button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
